import { useEffect, useState, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import AppBar from '@mui/material/AppBar';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import { NavLink } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ForkRightIcon from '@mui/icons-material/ForkRight';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import LocalizedStrings from 'react-localization';
import LOCALIZATIONS from './localizations';
import { PATH_ABOUT, PATH_BOARD, PATH_COLLECTION, PATH_MARKETPLACE, PATH_OFFERS, PATH_VIEW, TOKEN_ID } from '../../router/config';
import './style.css';
import { FORK, OPEN, SAVE } from '../../redux/actions/board';
import { MODAL_TOGGLE } from '../../redux/actions/app';
import { useSnackbar } from 'notistack';
import { isValid } from '../../util/board';
import { ID as NEW_BOARD_ID } from '../NewBoard/config';
import ConnectMetaMask from '../ConnectMetaMask';
import { ID as MINT_ID } from '../Mint/config';
import { AboutIcon, CollectionIcon, MarketIcon, MintIcon, OfferIcon } from '../../util/icons';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { useTheme, styled } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useLocation, useNavigate } from 'react-router-dom';
import Drawer from '@mui/material/Drawer';
import Badge from '@mui/material/Badge';
import Chip from '@mui/material/Chip';
import Link from '@mui/material/Link';
import { dispatchErr, getBoardFromContract } from '../../util/metamask';
import TelegramIcon from '@mui/icons-material/Telegram';
import { channelUrl } from '../../util/telegram';

const DrawerHeader = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(0, 1),
    ...theme.mixins.toolbar,
    justifyContent: 'flex-end',
}));

const Masthead = () => {

    const anchorOrigin = useMemo(() => ({ vertical: 'top', horizontal: 'left' }), []),
    classes = [ "coe__masthead" ],
    dispatch = useDispatch(),
    { enqueueSnackbar } = useSnackbar(),
    theme = useTheme(),
    isMobile = useMediaQuery( theme.breakpoints.down('md') ),
    labels = useMemo(() => new LocalizedStrings( LOCALIZATIONS ), []),
    locale = useSelector( state => state.app.locale ),
    { isNew, dragging } = useSelector( state => state.board ),
    [ anchorEl, setAnchorEl ] = useState( null ),
    { accounts, newOffers } = useSelector( state => state.eth ),
    open = Boolean( anchorEl ),
    offersExist = ( newOffers.sell > 0 || newOffers.buy > 0 ),
    fileRef = useRef( null ),
    navigate = useNavigate(),
    { pathname } = useLocation(),
    viewing = ( pathname.includes( PATH_VIEW.replace( TOKEN_ID, '' ) ) ); 

    /**
     * @name createNew
     * @function
     * @description Initiates a new board
     */
    const createNew = () => {
        if( !isNew ) dispatch({ type: MODAL_TOGGLE, payload: NEW_BOARD_ID });
        navigate( PATH_BOARD );
        toggleMenu( true )();
    }

    /**
     * @name fork
     * @function
     * @description Forks an NFT
     */
    const fork = async () => {
        const tokenId = pathname.match( /[0-9]+/ )[ 0 ];
        toggleMenu( true )();
        enqueueSnackbar(
            labels.attemptingFork,
            { variant: 'info' }
        )
        try {
            const board = await getBoardFromContract( tokenId );
            enqueueSnackbar(
                labels.formatString(
                    labels.successfulFork,
                    board.name
                ),
                { variant: 'info' }
            )
            dispatch({ type: FORK, payload: { board, tokenId } });
            navigate( PATH_BOARD );
        } catch( err ){
            dispatchErr( err, enqueueSnackbar, labels );
        }
        
    }

    /**
     * @name handleUpload
     * @function
     * @description Removes the data item
     */
     const handleUpload = e => {
        navigate( PATH_BOARD );
        const fr = new FileReader(),
        fileName = e.target.files[0].name;
        fr.addEventListener( 'load', () => {
            try {
                const payload = JSON.parse( fr.result );

                // validate the contents so we dont break everything
                if( isValid( payload ) ){
                    enqueueSnackbar(
                        labels.formatString(
                            labels.fileLoaded,
                            fileName
                        ),
                        { variant: 'info' }
                    );
                    dispatch({ type: OPEN, payload }); 
                } else {
                    enqueueSnackbar(
                        labels.fileIncorrect,
                        { variant: 'error' }
                    );
                }
            
            } catch( err ) {
                console.error( err );
                enqueueSnackbar(
                    labels.fileCorrupt,
                    { variant: 'error' }
                );
            }
        });
        fr.readAsText( e.target.files[0] );
        e.target.value = '';
    }

    /**
     * @name mint
     * @function
     * @description Initiates the board minting process
     */
    const mint = () => {
        dispatch({ type: MODAL_TOGGLE, payload: MINT_ID });
        toggleMenu( true )();
    }

    /**
     * @name openBoard
     * @function
     * @description Initiates the opening of a board
     */
    const openBoard = () => {
        fileRef.current.click();
        toggleMenu( true )();
    }

    /**
     * @name save
     * @function
     * @description Saves the current board
     */
    const save = () => {
        dispatch({ type: SAVE });
        toggleMenu( true )();
    }

    /**
     * @name toggleMenu
     * @function
     * @description Sets the target element for the menu
     * @param {EventListenerObject} e 
     */
    const toggleMenu = close => e => {
        setAnchorEl( !close ? e.currentTarget : null );
    }

    // update locale
    useEffect(() => {
        if( locale !== labels.getLanguage() ) labels.setLanguage( locale )
    }, [ labels, locale ]);

    const menuItems = [
        {
            icon: <AddCircleOutlineIcon />,
            label: labels.startNew,
            onClick: createNew
        },
        {
            icon: <FolderOpenIcon />,
            label: labels.open,
            onClick: openBoard
        },
        {
            divider: true
        },
        {
            disabled: ( isNew || viewing ),
            icon: <FileDownloadIcon />,
            label: labels.download,
            onClick: save
        },
        {
            disabled: ( isNew || accounts.length === 0 || viewing ),
            icon: <MintIcon />,
            label: labels.mint,
            onClick: mint
        },
        {
            disabled: !viewing,
            icon: <ForkRightIcon />,
            label: labels.fork,
            onClick: fork
        },
        {
            divider: true
        },
        {
            disabled: accounts.length === 0,
            icon: <CollectionIcon />,
            label: labels.collection,
            onClick: toggleMenu( true ),
            to: PATH_COLLECTION
        },
        {
            disabled: accounts.length === 0,
            icon: <OfferIcon />,
            get count() {
                let total = 0;
                total += newOffers.sell;
                total += newOffers.buy;

                return ( total === 0 ) ? null : total;
            },
            label: labels.offers,
            onClick: toggleMenu( true ),
            to: PATH_OFFERS
        },
        {
            icon: <MarketIcon />,
            label: labels.marketplace,
            onClick: toggleMenu( true ),
            to: PATH_MARKETPLACE
        },
        {
            divider: true
        },
        {
            icon: <AboutIcon />,
            label: labels.about,
            onClick: toggleMenu( true ),
            to: PATH_ABOUT
        },
        {
            divider: true
        },
        {
            icon: <TelegramIcon />,
            label: labels.telegram,
            onClick: toggleMenu( true ),
            href: channelUrl
        }
    ];

    // modifiers
    if( dragging ) classes.push( classes[ 0 ] + '--dragging' );

    return (
        <>
            <input hidden accept="application/json" multiple ref={ fileRef } onChange={ handleUpload } type="file" />
            <AppBar position='sticky' className={ classes.join( ' ' ) }>
                <Toolbar>
                    <IconButton
                        aria-label="menu"
                        color="inherit"
                        edge="start"
                        onClick={ toggleMenu( false ) }
                        size="large"
                        sx={{ mr: 2 }}
                    >
                        <Badge anchorOrigin={ anchorOrigin } color="error" variant="dot" invisible={ !offersExist }>
                            <MenuIcon />
                        </Badge>
                    </IconButton>
                    
                    <Typography variant="h6" component="h1" sx={{ flexGrow: 1 }}>
                        <NavLink className="coe__masthead__brand" to={ PATH_BOARD }>{ labels.appName }</NavLink>
                    </Typography>
                    
                    {
                        !isMobile || ( isMobile && window.ethereum ) ?
                            <ConnectMetaMask /> :
                            null
                    }
                </Toolbar>
            </AppBar>
            {
                // desktop menu
                !isMobile ?
                    <Menu
                        anchorEl={ anchorEl }
                        className="coe__mainMenu"
                        MenuListProps={{
                            'aria-labelledby': 'basic-button',
                        }}
                        onClose={ toggleMenu( true ) }
                        open={ open }>
                            {
                                menuItems.map( 
                                    ( item, index ) => {

                                        const key = 'desktop-menu-' + index;

                                        var component = null;

                                        if( item.to ) component = NavLink;
                                        if( item.href ) component = Link;

                                        if( item.divider ) return ( <Divider key={ key } /> );

                                        return (
                                            <MenuItem 
                                                component={ component } 
                                                href={ item.href } 
                                                key={ key } 
                                                to={ item.to } 
                                                onClick={ item.onClick } 
                                                disabled={ item.disabled }
                                                target={ item.href ? '_blank' : null }>
                                                <ListItemIcon>
                                                    { item.icon }
                                                </ListItemIcon>
                                                <ListItemText>
                                                    { item.label }
                                                    {
                                                        item.count ? 
                                                            <Chip className="coe__masthead__count" color="error" label={ item.count } size="small" /> :
                                                            null
                                                    }
                                                </ListItemText>
                                            </MenuItem>
                                        )
                                    }
                                )
                            }
                    </Menu> :
                    // mobile menu
                    <Drawer
                        anchor="left"
                        open={ open }
                        onClose={ toggleMenu( true ) }>
                        <DrawerHeader>
                            <IconButton onClick={ toggleMenu( true ) } aria-label={ labels.close }>
                                <ArrowBackIosNewIcon />
                            </IconButton>
                        </DrawerHeader>
                        <Divider />
                        <List>
                            {
                                menuItems.map( 
                                    ( item, index ) => {

                                        const key = 'mobile-menu-' + index;
                                        var component = null;

                                        if( item.to ) component = NavLink;
                                        if( item.href ) component = Link;

                                        if( item.divider ) return ( <Divider key={ key } /> );

                                        return (
                                            <ListItem key={ key } disablePadding>
                                                <ListItemButton component={ component } href={ item.href } to={ item.to } onClick={ item.onClick } disabled={ item.disabled } target={ item.href ? '_blank' : null }>
                                                    <ListItemIcon>
                                                        { item.icon }
                                                    </ListItemIcon>
                                                    <ListItemText>
                                                        { item.label }
                                                        {
                                                            item.count ? 
                                                                <Chip className="coe__masthead__count" color="error" label={ item.count } size="small" /> :
                                                                null
                                                        }
                                                    </ListItemText>
                                                </ListItemButton>
                                            </ListItem>
                                        )
                                    }
                                )
                            }
                        </List>
                    </Drawer>

            }
           
        </>
    )
}

export default Masthead